import React, { useContext, useEffect, useState } from 'react';
import Tr from '../shared/tr';
import Radio from '@material-ui/core/Radio';
import Slider from '@material-ui/core/Slider';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import { Theme } from '@material-ui/core/styles/createMuiTheme';
import { makeStyles, createStyles, withStyles } from '@material-ui/core/styles';

import { AppContext } from '../App';
import TooltipButton from '../shared/TooltipButton';

import ShowIcon from '../../assets/image/toolpanel/show.svg';
import HideIcon from '../../assets/image/toolpanel/hide.svg';
import { mat4 } from 'gl-matrix';
import { MeshName } from '../shared/common';
import { Mesh } from '../model/Mesh';

const TransparencySlider = withStyles({
  root: {
    color: '#434343',
  },
  thumb: {
    height: 16,
    width: 16,
    backgroundColor: '#262626',
  },
  track: {
    height: 4,
  },
  rail: {
    height: 4,
  },
})(Slider);

const useDentalArchStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {},
  })
);

const DentalArch = ({ mesh }) => {
  const classes = useDentalArchStyles();

  const [transparency, setTransparency] = React.useState<number | string | Array<number | string>>(0);
  const [visible, setVisible] = useState(true);
  useEffect(() => {
    mesh.setVisible(visible);
    window.dispatchEvent(new Event('refreshCanvas'));
  }, [visible]);

  useEffect(() => {
    mesh.setTransparency(transparency);
    window.dispatchEvent(new Event('refreshCanvas'));
  }, [transparency]);

  const handleTransparencyChange = (event: any, newValue: number | number[]) => {
    setTransparency(newValue as number);
  };

  const tr = new Tr();

  return (
    <Grid item container spacing={0}>
      <Grid item xs={12}>
        <Typography variant="subtitle1" className={classes.title}>
          {tr.meshName(mesh.name)}
        </Typography>
      </Grid>
      <Grid item container xs={12} spacing={3} justify="center" alignItems="center">
        <Grid item xs={2}>
          <TooltipButton
            title={visible ? tr.displayShow() : tr.displayHide()}
            placement="right"
            imgSrc={visible ? ShowIcon : HideIcon}
            onClick={() => {
              setVisible((v) => !v);
            }}
          />
        </Grid>
        <Grid item xs={8}>
          <TransparencySlider
            value={typeof transparency === 'number' ? transparency : 0}
            onChange={handleTransparencyChange}
            aria-labelledby="transparency-slider"
          ></TransparencySlider>
        </Grid>
        <Grid item xs={2}>
          <Typography variant="subtitle1">{transparency}</Typography>
        </Grid>
      </Grid>
    </Grid>
  );
};

const useBuccalBiteStyles = makeStyles((theme: Theme) =>
  createStyles({
    title: {},
    radio: {
      '&$checked': {
        color: '#36B0C9',
      },
    },
    checked: {},
  })
);

const BuccalBite = ({ bite, selectedBite, setSelectedBite, setSelectedBiteMeshVisible }) => {
  const classes = useBuccalBiteStyles();

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedBite(event.target.value);
    setSelectedBiteMeshVisible(meshVisible);
  };

  const [transparency, setTransparency] = React.useState<number | string | Array<number | string>>(0);

  const [meshVisible, setMeshVisible] = useState(false);

  useEffect(() => {
    bite.setVisible(meshVisible);
    window.dispatchEvent(new Event('refreshCanvas'));
  }, [meshVisible]);

  useEffect(() => {
    bite.setTransparency(transparency);
    window.dispatchEvent(new Event('refreshCanvas'));
  }, [transparency]);

  const handleTransparencyChange = (event: any, newValue: number | number[]) => {
    setTransparency(newValue as number);
  };

  const tr = new Tr();

  return (
    <Grid item xs={12} container spacing={0}>
      <Grid item container xs={12} spacing={3} justify="center" alignItems="center">
        <Grid item xs={2}>
          <Radio
            checked={selectedBite === bite.name}
            onChange={handleChange}
            value={bite.name}
            name="radio-button"
            classes={{ root: classes.radio, checked: classes.checked }}
          />
        </Grid>
        <Grid item xs={10}>
          <Typography variant="subtitle1" className={classes.title}>
            {bite.name}
          </Typography>
        </Grid>
      </Grid>
      {bite.actor && (
        <Grid item container xs={12} spacing={3} justify="center" alignItems="center">
          {selectedBite === bite.name && (
            <Grid item xs={2}>
              <TooltipButton
                title={meshVisible ? tr.displayShow() : tr.displayHide()}
                placement="right"
                imgSrc={meshVisible ? ShowIcon : HideIcon}
                onClick={() => {
                  setMeshVisible((v) => !v);
                  setSelectedBiteMeshVisible(meshVisible);
                }}
              />
            </Grid>
          )}
          {selectedBite === bite.name && (
            <Grid item xs={8}>
              <TransparencySlider
                value={typeof transparency === 'number' ? transparency : 0}
                onChange={handleTransparencyChange}
                aria-labelledby="transparency-slider"
              ></TransparencySlider>
            </Grid>
          )}
          {selectedBite === bite.name && (
            <Grid item xs={2}>
              <Typography variant="subtitle1">{transparency}</Typography>
            </Grid>
          )}
        </Grid>
      )}
    </Grid>
  );
};

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      height: '100%',
      overflowY: 'auto',
      overflowX: 'hidden',
    },
    grid: {
      width: '100%',
      margin: '20px 20px 0 0 ',
    },
  })
);

const Display = () => {
  const { normalMeshes, buccalMeshes, setRefreshCanvas } = useContext(AppContext);
  const [selectedBite, setSelectedBite] = React.useState(MeshName.NormalBite);
  const [selectedBiteMeshVisible, setSelectedBiteMeshVisible] = React.useState(false);

  useEffect(() => {
    buccalMeshes.forEach((bite) => {
      const isSelected = bite.name === selectedBite;
      if (isSelected) {
        normalMeshes.forEach((mesh) => {
          applyBiteOrAdjustments(mesh, bite);
        });
      }
      bite.setVisible(isSelected && selectedBiteMeshVisible);
    });
    setRefreshCanvas(true);
  }, [selectedBite]);

  const classes = useStyles();
  const tr = new Tr();

  const applyBiteOrAdjustments = (mesh: Mesh, bite: Mesh) => {
    if (!mesh || !bite) {
      return;
    }
    const mat = mat4.create();
    if (bite.name !== MeshName.NormalBite) {
      mesh.doTransformWithMatrix(mat);
      if (mesh.isLowerJaw()) {
        mat4.copy(mat, bite.matrix);
        mesh.doTransformWithMatrix(mat);
      }
    } else {
      // normal bite
      if (mesh.hasAdjustments()) {
        mesh.applyAdjustments();
      } else if (mesh.isLowerJaw()) {
        mat4.copy(mat, bite.matrix);
        mesh.doTransformWithMatrix(mat);
      }
    }
  };

  return (
    <div className={classes.root}>
      <Grid container spacing={1} alignItems="center" justify="center" className={classes.grid}>
        <Grid item xs={12}>
          <Typography gutterBottom variant="h5">
            <b>{tr.titlebarDisplay()}</b>
          </Typography>
        </Grid>

        {normalMeshes.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h5">{tr.displayDentalArch()}</Typography>
          </Grid>
        )}
        {normalMeshes.length > 0 && (
          <Grid item xs={12}>
            {normalMeshes.map((mesh, index) => {
              return <DentalArch key={index} mesh={mesh} />;
            })}
          </Grid>
        )}

        {buccalMeshes.length > 0 && (
          <Grid item xs={12}>
            <Typography variant="h5">{tr.displayBuccalBite()}</Typography>
          </Grid>
        )}
        {buccalMeshes.length > 0 && (
          <Grid item xs={12}>
            {buccalMeshes.map((bite, index) => {
              return (
                <BuccalBite
                  key={index}
                  bite={bite}
                  selectedBite={selectedBite}
                  setSelectedBite={setSelectedBite}
                  setSelectedBiteMeshVisible={setSelectedBiteMeshVisible}
                />
              );
            })}
          </Grid>
        )}
      </Grid>
    </div>
  );
};

export default Display;
